/**
 * M5T Chat Prompt Area Web Component
 * User input area for chat interface with auto-expanding textarea
 *
 * Shared component - can be used across multiple client apps
 *
 * Features:
 * - Pill-shaped container with CSS Grid layout
 * - Three columns: Cancel | Prompt (textarea) | Send
 * - Auto-expanding textarea (grows with content)
 * - Dispatches 'message-sent' custom event with pubsubTopic
 * - WebComponentBridge publishes to CONVERSATION.PROMPT.AGENT_CANVAS
 *
 * Attributes:
 * - placeholder: string - Placeholder text for textarea (default: "Type your message...")
 * - max-rows: number - Maximum rows before scrolling (default: 5)
 *
 * Usage:
 *   <m5t-chat-prompt-area placeholder="Ask me anything..."></m5t-chat-prompt-area>
 *
 * Custom Events:
 *   Dispatches: 'message-sent' with detail: { message, timestamp, pubsubTopic }
 *   WebComponentBridge publishes to: CONVERSATION.PROMPT.AGENT_CANVAS
 */

class M5TChatPromptArea extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
  }

  /**
   * Get placeholder text
   */
  getPlaceholder() {
    return this.getAttribute('placeholder') || 'Type your message...';
  }

  /**
   * Get max rows before scrolling
   */
  getMaxRows() {
    return parseInt(this.getAttribute('max-rows')) || 5;
  }

  /**
   * Render the component
   */
  render() {
    const placeholder = this.getPlaceholder();
    const maxRows = this.getMaxRows();

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
          padding: 4px 32px;
        }

        .prompt-container {
          display: grid;
          grid-template-columns: 48px 1fr 48px;
          grid-template-areas: "cancel prompt send";
          gap: 12px;
          background: #2d2d2d;
          border: 2px solid #3a3a3a;
          border-radius: 28px;
          padding: 8px 12px;
          align-items: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
          transition: all 0.3s ease;
        }

        .prompt-container:focus-within {
          border-color: #92a8d1;
          box-shadow: 0 4px 16px rgba(146, 168, 209, 0.3);
        }

        .cancel-btn {
          grid-area: cancel;
          width: 48px;
          height: 48px;
          background: #92a8d1;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .cancel-btn:hover {
          background: #364C75;
          transform: scale(1.05);
        }

        .cancel-btn:active {
          transform: scale(0.95);
        }

        .prompt-area {
          grid-area: prompt;
          background: transparent;
          border: none;
          color: #e4e4e4;
          font-size: 1rem;
          font-family: inherit;
          line-height: 1.5;
          resize: none;
          overflow-y: auto;
          outline: none;
          padding: 8px 0;
          min-height: 24px;
          max-height: calc(1.5rem * ${maxRows});
        }

        .prompt-area::placeholder {
          color: #a8a8a8;
        }

        .prompt-area::-webkit-scrollbar {
          width: 6px;
        }

        .prompt-area::-webkit-scrollbar-track {
          background: transparent;
        }

        .prompt-area::-webkit-scrollbar-thumb {
          background: #3a3a3a;
          border-radius: 3px;
        }

        .send-btn {
          grid-area: send;
          width: 48px;
          height: 48px;
          background: #92a8d1;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .send-btn:hover {
          background: #364C75;
          transform: scale(1.05);
        }

        .send-btn:active {
          transform: scale(0.95);
        }

        .send-btn:disabled,
        .cancel-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        /* Icons inherit the button's color property, which flips with the
           background so the glyph keeps its contrast in both states: dark on
           the light periwinkle rest state, light on the dark navy hover. */
        .cancel-btn,
        .send-btn {
          color: #1a1a1a;
        }

        .cancel-btn:hover,
        .send-btn:hover {
          color: #e4e4e4;
        }

        .cancel-btn svg,
        .send-btn svg {
          width: 22px;
          height: 22px;
          display: block;
          /* Clicks resolve to the button, never the glyph. */
          pointer-events: none;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          :host {
            padding: 4px 16px;
          }

          .prompt-container {
            gap: 8px;
            padding: 6px 10px;
          }

          .cancel-btn,
          .send-btn {
            width: 40px;
            height: 40px;
          }

          .cancel-btn svg,
          .send-btn svg {
            width: 18px;
            height: 18px;
          }

          .prompt-area {
            font-size: 0.95rem;
          }
        }
      </style>

      <div class="prompt-container">
        <button class="cancel-btn" id="cancel-btn" type="button" title="Clear" aria-label="Clear the message">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M18 6 6 18"/>
            <path d="m6 6 12 12"/>
          </svg>
        </button>

        <textarea
          class="prompt-area"
          id="prompt-textarea"
          placeholder="${placeholder}"
          rows="1"
        ></textarea>

        <button class="send-btn" id="send-btn" type="button" title="Send (Enter)" aria-label="Send message">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"/>
            <path d="m21.854 2.147-10.94 10.939"/>
          </svg>
        </button>
      </div>
    `;
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    const textarea = this.shadowRoot.getElementById('prompt-textarea');
    const sendBtn = this.shadowRoot.getElementById('send-btn');
    const cancelBtn = this.shadowRoot.getElementById('cancel-btn');

    if (!textarea || !sendBtn || !cancelBtn) return;

    // Auto-expand textarea as user types
    textarea.addEventListener('input', () => {
      this.autoExpandTextarea(textarea);
    });

    // Send on Enter (Shift+Enter for new line)
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSend();
      }
    });

    // Send button click
    sendBtn.addEventListener('click', () => {
      this.handleSend();
    });

    // Cancel button click
    cancelBtn.addEventListener('click', () => {
      this.handleCancel();
    });
  }

  /**
   * Auto-expand textarea based on content
   */
  autoExpandTextarea(textarea) {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Set height to scrollHeight (content height)
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  /**
   * Handle send action
   * Dispatches custom event - WebComponentBridge publishes to CONVERSATION.PROMPT.AGENT_CANVAS
   */
  handleSend() {
    const textarea = this.shadowRoot.getElementById('prompt-textarea');
    if (!textarea) return;

    const message = textarea.value.trim();

    if (!message) {
      console.warn('[M5TChatPromptArea] Cannot send empty message');
      return;
    }

    const timestamp = new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });

    const payload = {
      message,
      timestamp,
      pubsubTopic: 'CONVERSATION.PROMPT.AGENT_CANVAS'
    };

    console.log('[M5TChatPromptArea] Dispatching message-sent event:', payload);

    // Clear textarea
    textarea.value = '';
    textarea.style.height = 'auto';

    // Dispatch custom event - WebComponentBridge will publish to PubSub
    this.dispatchEvent(new CustomEvent('message-sent', {
      bubbles: true,
      composed: true,
      detail: payload
    }));
  }

  /**
   * Handle cancel action
   */
  handleCancel() {
    const textarea = this.shadowRoot.getElementById('prompt-textarea');
    if (!textarea) return;

    // Clear textarea
    textarea.value = '';
    textarea.style.height = 'auto';

    console.log('[M5TChatPromptArea] Input cancelled');

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('message-cancelled', {
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Get current message (for external access)
   */
  getMessage() {
    const textarea = this.shadowRoot.getElementById('prompt-textarea');
    return textarea ? textarea.value : '';
  }

  /**
   * Set message (for external control)
   */
  setMessage(message) {
    const textarea = this.shadowRoot.getElementById('prompt-textarea');
    if (textarea) {
      textarea.value = message;
      this.autoExpandTextarea(textarea);
    }
  }

  /**
   * Clear message
   */
  clear() {
    this.handleCancel();
  }

  /**
   * Focus textarea
   */
  focus() {
    const textarea = this.shadowRoot.getElementById('prompt-textarea');
    if (textarea) {
      textarea.focus();
    }
  }
}

// Define the custom element
customElements.define('m5t-chat-prompt-area', M5TChatPromptArea);

export default M5TChatPromptArea;
